import type { DistributionSummary } from '@projections/engine';
import { useMemo } from 'react';
import { formatCompactCurrency, formatCurrency } from '../design/format';
import { EChart } from './EChart';
import { type ProjectionTokens, resolveTokens } from './theme';

/**
 * TERMINAL-WEALTH HISTOGRAM (PLAN.md M7) — `--accent` bars + a median marker.
 *
 * Bins the per-path final-year end balances (`ResultSet.terminalWealth`, sorted ascending) into a
 * histogram, with a vertical markLine at the median (from `terminalWealthSummary.median`). Bars use
 * the accent; the median line is dashed `--ink-muted`. Tooltip shows the bin range + path count.
 */
export interface TerminalWealthHistogramProps {
  /** Per-path terminal wealth, sorted ascending (`ResultSet.terminalWealth`). */
  terminalWealth: readonly number[];
  /** Distribution summary for the median marker (`ResultSet.terminalWealthSummary`). */
  summary?: DistributionSummary | undefined;
  /** Number of histogram bins. Default 30. */
  bins?: number;
  height?: number;
}

export function TerminalWealthHistogram({
  terminalWealth,
  summary,
  bins = 30,
  height = 300,
}: TerminalWealthHistogramProps): React.ReactElement {
  const option = useMemo(
    () => buildHistogramOption(terminalWealth, bins, summary),
    [terminalWealth, bins, summary],
  );
  return (
    <EChart option={option} height={height} aria-label="Terminal wealth distribution histogram" />
  );
}

export interface HistogramBin {
  /** Inclusive lower edge of the bin (plan currency). */
  readonly lo: number;
  /** Exclusive upper edge of the bin (the last bin is inclusive of `max`). */
  readonly hi: number;
  /** Number of paths whose terminal wealth fell in `[lo, hi)`. */
  readonly count: number;
}

/**
 * Bin a SORTED-ASCENDING value array into `bins` equal-width buckets between min and max. PURE +
 * exported so the histogram bin maths can be unit-tested independently of ECharts. Degenerate
 * inputs (empty, all-equal) collapse to a single bin so the chart never divides by zero.
 */
export function binValues(sorted: readonly number[], bins: number): HistogramBin[] {
  const n = sorted.length;
  if (n === 0) return [];
  const min = sorted[0] as number;
  const max = sorted[n - 1] as number;
  const count = Math.max(1, Math.floor(bins));
  if (max === min) {
    return [{ lo: min, hi: min, count: n }];
  }
  const width = (max - min) / count;
  const out: HistogramBin[] = Array.from({ length: count }, (_, i) => ({
    lo: min + i * width,
    hi: min + (i + 1) * width,
    count: 0,
  })) as HistogramBin[];
  for (const v of sorted) {
    // Clamp the rightmost edge into the last bin (max would otherwise land at index === count).
    let idx = Math.floor((v - min) / width);
    if (idx >= count) idx = count - 1;
    if (idx < 0) idx = 0;
    const bin = out[idx] as HistogramBin;
    (out[idx] as { count: number }).count = bin.count + 1;
  }
  return out;
}

/**
 * Build the histogram ECharts option. PURE — exported for unit testing.
 *
 * Bins `terminalWealth` into `bins` equal-width buckets, emits an accent bar series, adds a dashed
 * `markLine` at `summary.median`, labels the x-axis with `formatCompactCurrency` bin centres, and a
 * tooltip with the full `formatCurrency` range + path count.
 *
 * `tokens` defaults to the live design-system tokens (`resolveTokens()`); tests pass a fixture so
 * the option shape can be asserted with no DOM.
 */
export function buildHistogramOption(
  values: readonly number[],
  bins: number,
  summary?: DistributionSummary,
  tokens: ProjectionTokens = resolveTokens(),
): Record<string, unknown> {
  const histogram = binValues(values, bins);
  const centres = histogram.map((b) => (b.lo + b.hi) / 2);
  const counts = histogram.map((b) => b.count);
  const labels = centres.map((c) => formatCompactCurrency(c));

  const median = summary?.median;
  const markLine =
    median !== undefined
      ? {
          silent: true,
          symbol: 'none',
          lineStyle: { color: tokens.inkMuted, type: 'dashed', width: 1 },
          label: {
            formatter: `median ${formatCompactCurrency(median)}`,
            color: tokens.inkMuted,
            fontFamily: tokens.fontMono,
            fontSize: 11,
            position: 'insideEndTop',
          },
          // Place on the value (category) axis at the nearest bin centre to the median.
          data: [{ xAxis: nearestIndex(centres, median) }],
        }
      : undefined;

  const series: Record<string, unknown> = {
    name: 'Paths',
    type: 'bar',
    data: counts,
    barCategoryGap: '12%',
    itemStyle: { color: tokens.accent, borderRadius: [2, 2, 0, 0] },
  };
  if (markLine !== undefined) series.markLine = markLine;

  return {
    animation: true,
    animationDuration: 600,
    grid: { left: 8, right: 16, top: 16, bottom: 8, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: unknown) => {
        const arr = Array.isArray(params) ? params : [params];
        const first = arr[0] as { dataIndex?: number } | undefined;
        const i = first?.dataIndex ?? -1;
        const bin = histogram[i];
        if (bin === undefined) return '';
        const range =
          bin.lo === bin.hi
            ? formatCurrency(bin.lo)
            : `${formatCurrency(bin.lo)} – ${formatCurrency(bin.hi)}`;
        return `<div style="margin-bottom:4px">${range}</div><div style="font-weight:600">${bin.count.toLocaleString('en-US')} paths</div>`;
      },
    },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: { interval: Math.max(0, Math.floor(labels.length / 8)) },
    },
    yAxis: {
      type: 'value',
      name: 'paths',
      nameTextStyle: { color: tokens.inkMuted, fontFamily: tokens.fontUi, fontSize: 11 },
    },
    series: [series],
  };
}

/** Index of the value in `arr` closest to `target` (arr assumed non-empty for a marker). */
function nearestIndex(arr: readonly number[], target: number): number {
  let best = 0;
  let bestD = Number.POSITIVE_INFINITY;
  for (let i = 0; i < arr.length; i++) {
    const d = Math.abs((arr[i] as number) - target);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}
